import { useCompanyFeatures } from './useCompanyFeatures';
import type { CompanyInfo } from '@scio3/shared-types';

/**
 * Which permission MODEL this account runs (#2452).
 *
 *   true  — the account was created on or after the Group Security release. It gets the Directory
 *           (Users / Teamspaces / Roles / Groups) and the Group Security permission path.
 *   false — everything else, which today is every existing customer. They keep production's
 *           behaviour exactly: the Account Members page, Shared Team, the legacy folder path.
 *
 * **ABSENT reads as false, and that is the entire backfill strategy** — nothing ever writes
 * `false` to the field, so an account that predates the release simply has no key. Any new reader
 * must preserve that: `?? false`, never `!== false`.
 *
 * ── Two things that look like bugs and are not ──────────────────────────────────────────────────
 *
 * 1. NOT the same question as `groupSecurity`, one word away in the same object. That one is "has
 *    the admin opted in?" (the Activate Groups card — reversible, plan-derived). This one is "which
 *    code path serves the request?" — written once at account creation and never again. An ON-cohort
 *    account still starts behind the Activate Groups card (spec §11, D6).
 *
 * 2. No `loading` flag, deliberately. `ProtectedRoute` renders nothing until `auth-store.loading`
 *    clears, and `AuthProvider.loadUserContext` clears it only AFTER `setCompany(...)` — so by the
 *    time any consumer of this hook mounts, the company is in the store. `useUserManagementAccess`
 *    reads `folderLevelSecurity` on the same assumption. If `getCompanyInfo` FAILS, company stays
 *    null and this reads false: the account falls back to legacy behaviour, which is production's
 *    behaviour. That is the safe direction for this release and is the reason no guard is added.
 *
 * Spec: claude-dev/ted/2026-07-23-group-security/fixbugs/2026-10-13-2452/01-spec.md §3a / §6
 */
export function useGroupSecurityCohort(): boolean {
  // `=== true`, not a pass-through. `getCompanyFeatures` already coerces, so this looks redundant —
  // it is not. Consumers put the result straight into `show:` expressions, and SettingsLayout's
  // renderer filters on `show !== false`: an `undefined` leaking through there RENDERS the row
  // instead of hiding it, i.e. the whole Directory appears for a legacy account. Caught by the
  // ABSENT case in SettingsLayout.test.tsx. This hook owns "absent ⇒ false"; it does not borrow it.
  return useCompanyFeatures().groupSecurityCohort === true;
}

/**
 * Pure form, for callers that already hold the company (route tests, non-React code). Same rule:
 * absent ⇒ false.
 */
export function isGroupSecurityCohort(company: CompanyInfo | null): boolean {
  // Reads the field directly rather than going through `getCompanyFeatures`. Same answer (absent
  // and null both give `false`), but no dependency on that module — which matters because
  // `usePermissions` imports this, and every component test that mocks `@/hooks/useCompanyFeatures`
  // would otherwise have to add a `getCompanyFeatures` export it has no interest in. Keeping the
  // pure predicate dependency-free keeps the single-reader rule cheap to honour.
  return company?.feature?.groupSecurityCohort === true;
}
